/**
 * QuickInvoiceModal.jsx — Compact modal for deposit/balance/full invoice creation
 *
 * Used by pipeline steps 3 (deposit) & 11 (balance or full — 'full' when the
 * job never had a deposit). Offers two actions:
 *   - Create        → saves invoice, closes
 *   - Create & Send → saves invoice, invokes parent onCreatedAndSend(inv)
 *                     (which awaits Xero sync and opens mailto + records sentAt)
 *
 * Advanced fields (amount / due date / notes) collapsed behind a "Customize"
 * toggle — defaults are correct for 99% of cases.
 */

import { useState, useMemo, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, Stack, Collapse, IconButton, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { uid, fmtC } from '../../lib/utils';
import { defaultDueDate, termsLabel } from '../../lib/invoiceDefaults';
import { useCreateInvoice, useInvoices } from '../../hooks/useInvoices';
import { useXeroStatus, useSyncInvoiceToXero } from '../../hooks/useXero';
import { useToast } from '../layout/Toast';

export default function QuickInvoiceModal({
  open,
  onClose,
  type,              // 'deposit' | 'balance' | 'full'
  job,
  settings,          // unwrapped settings.data (the JobPipeline pattern)
  existingInvoices = [],
  onCreatedAndSend,  // async (invoice) => Promise — parent handles mailto + sentAt
}) {
  const { toast } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: allInvoices = [] } = useInvoices();
  const createMutation = useCreateInvoice();
  const { data: xeroStatus } = useXeroStatus();
  const syncMutation = useSyncInvoiceToXero();
  const xeroConnected = xeroStatus?.connected === true;

  const depositPct = parseFloat(settings?.depositPct || 50);
  const jobRevenue = parseFloat(job?.revenue || 0);

  // ── Compute defaults ─────────────────────────────────────────────────────
  const existingDeposit = existingInvoices.find(
    (inv) => inv.invoiceType === 'deposit' && inv.status !== 'void'
  );

  const defaultAmount = useMemo(() => {
    if (type === 'deposit') {
      return Math.round(jobRevenue * (depositPct / 100) * 100) / 100;
    }
    if (type === 'full') {
      // Full invoice = entire job revenue (no deposit ever created)
      return Math.max(0, Math.round(jobRevenue * 100) / 100);
    }
    // balance
    const depositAmt = (existingDeposit && existingDeposit.status === 'paid')
      ? parseFloat(existingDeposit.amount || 0)
      : (existingDeposit ? 0 : Math.round(jobRevenue * (depositPct / 100) * 100) / 100);
    return Math.max(0, Math.round((jobRevenue - depositAmt) * 100) / 100);
  }, [type, jobRevenue, depositPct, existingDeposit]);

  // ── Form state ───────────────────────────────────────────────────────────
  const [amount, setAmount] = useState(defaultAmount);
  const [dueDate, setDueDate] = useState(() => defaultDueDate(type, settings));
  const [notes, setNotes] = useState('');
  const [showCustomize, setShowCustomize] = useState(false);
  const [busy, setBusy] = useState(false);

  // Reset defaults when modal opens or type changes. Keyed on the derived
  // date string so an unrelated settings save can't wipe in-progress edits.
  const typeDueDate = defaultDueDate(type, settings);
  useEffect(() => {
    if (open) {
      setAmount(defaultAmount);
      setDueDate(typeDueDate);
      setNotes('');
      setShowCustomize(false);
      setBusy(false);
    }
  }, [open, defaultAmount, typeDueDate]);

  // ── Invoice number generation (same logic as InvoiceForm) ────────────────
  const nextNumber = useMemo(() => {
    const prefix = settings?.invoicePrefix || 'INV';
    const startNum = parseInt(settings?.invoiceStart || '1', 10) || 1;
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const dateStr = `${yy}${mm}${dd}`;
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`^${escaped}-${dateStr}-(\\d+)$`);
    let maxNum = startNum - 1;
    allInvoices.forEach((inv) => {
      const match = (inv.number || '').match(pattern);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > maxNum) maxNum = num;
      }
    });
    return `${prefix}-${dateStr}-${String(maxNum + 1).padStart(3, '0')}`;
  }, [settings, allInvoices, open]);

  // ── Build + persist invoice ──────────────────────────────────────────────
  const buildPayload = () => ({
    id: uid(),
    number: nextNumber,
    jobId: job.id,
    client: job.clientName || job.client || '',
    invoiceType: type,
    amount: parseFloat(amount) || 0,
    paidAmount: 0,
    status: 'unpaid',
    terms: termsLabel(type, settings),
    dueDate,
    notes,
    payments: [],
  });

  const handleCreate = async (alsoSend) => {
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      toast('Amount must be greater than zero', 'error');
      return;
    }
    setBusy(true);
    const invoice = buildPayload();
    try {
      await createMutation.mutateAsync(invoice);
      if (alsoSend) {
        // Parent handles Xero sync + mailto + sentAt. Pass created invoice.
        await onCreatedAndSend?.(invoice);
      } else {
        toast(`Invoice ${invoice.number} created — ${fmtC(amt)}`);
        if (xeroConnected) syncMutation.mutate(invoice.id);
      }
      onClose?.();
    } catch (err) {
      toast(`Failed to create invoice: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!job || !type) return null;

  const title = type === 'deposit'
    ? 'Create Deposit Invoice'
    : type === 'full'
      ? 'Create Full Invoice'
      : 'Create Balance Invoice';
  const summaryText = type === 'deposit'
    ? `${depositPct}% deposit · ${fmtC(defaultAmount)} of ${fmtC(jobRevenue)}`
    : type === 'full'
      ? `Full invoice · ${fmtC(defaultAmount)}`
      : `Balance due · ${fmtC(defaultAmount)} of ${fmtC(jobRevenue)}`;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 700, pr: 5 }}>
        {title}
        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontFamily: 'monospace' }}>
          {nextNumber}
        </Typography>
        <IconButton onClick={onClose} disabled={busy} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {/* Summary row */}
        <Box sx={{ p: 2, mb: 2, borderRadius: 1, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {job.title || 'Job'}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {summaryText}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Due {dueDate}{notes ? ` · ${notes.slice(0, 40)}${notes.length > 40 ? '…' : ''}` : ''}
          </Typography>
        </Box>

        {/* Customize toggle */}
        <Button
          size="small"
          onClick={() => setShowCustomize((v) => !v)}
          endIcon={showCustomize ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ textTransform: 'none', fontSize: '0.75rem', mb: 1 }}
        >
          {showCustomize ? 'Hide' : 'Customize'}
        </Button>

        <Collapse in={showCustomize}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Amount"
              type="number"
              size="small"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ step: '0.01', min: '0' }}
              fullWidth
            />
            <TextField
              label="Due date"
              type="date"
              size="small"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Notes (optional)"
              size="small"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </Collapse>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="outlined"
          onClick={() => handleCreate(false)}
          disabled={busy}
          sx={{ textTransform: 'none' }}
        >
          Create
        </Button>
        <Button
          variant="contained"
          onClick={() => handleCreate(true)}
          disabled={busy}
          sx={{ textTransform: 'none' }}
        >
          {busy ? 'Working…' : 'Create & Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
